import React, { useCallback } from 'react';
import type { Edge, Node } from '@xyflow/react';
import { ModalPageView } from '@cotera/client/app/components/app';
import { useModalManager } from '@cotera/client/app/components/app/modal-manager/modal-manager';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  isSyntheticWorkflowEdgeId,
  isWorkflowTriggerFlowNodeId,
} from './workflow-flow-node-ids';

export type WorkflowDeletion = {
  nodes: readonly Node[];
  edges: readonly Edge[];
};

type WorkflowDeleteSummary = {
  title: string;
  detail: string;
  confirmLabel: string;
};

const plural = (count: number, one: string, many: string): string =>
  `${count} ${count === 1 ? one : many}`;

/**
 * What to warn about, or `null` when the deletion needs no confirmation.
 *
 * Edge-only deletions return `null` deliberately. A connection is trivial to
 * redraw, so a dialog there would buy nothing and would train people to dismiss
 * this dialog without reading it — which is exactly what makes the node case
 * dangerous.
 */
export function summarizeWorkflowDeletion({
  nodes,
  edges,
}: WorkflowDeletion): WorkflowDeleteSummary | null {
  const steps = nodes.filter((node) => !isWorkflowTriggerFlowNodeId(node.id));
  const triggers = nodes.filter((node) => isWorkflowTriggerFlowNodeId(node.id));
  // Synthetic trigger→entry edges aren't persisted rows; they're derived from
  // which nodes have no other inbound edge, so they aren't a loss to report.
  const realEdges = edges.filter((edge) => !isSyntheticWorkflowEdgeId(edge.id));

  if (steps.length === 0 && triggers.length === 0) {
    return null;
  }

  // Triggers only: the binding is archived, the trigger itself survives and can
  // be re-attached. Saying "delete" there would overstate what happens.
  if (steps.length === 0) {
    return {
      title:
        triggers.length === 1
          ? 'Remove this trigger?'
          : `Remove ${plural(triggers.length, 'trigger', 'triggers')}?`,
      detail:
        triggers.length === 1
          ? 'It stops firing this workflow. The trigger itself is kept and can be attached again.'
          : 'They stop firing this workflow. The triggers themselves are kept and can be attached again.',
      confirmLabel: 'Remove',
    };
  }

  const title =
    steps.length === 1 && triggers.length === 0
      ? 'Delete this step?'
      : `Delete ${plural(steps.length, 'step', 'steps')}${
          triggers.length > 0
            ? ` and ${plural(triggers.length, 'trigger', 'triggers')}`
            : ''
        }?`;

  // The connection count is the part worth reading: deleting a step in the middle
  // of a chain severs the graph either side of it, which isn't visible from the
  // node itself.
  const consequences: string[] = [];
  if (realEdges.length > 0) {
    consequences.push(
      `${plural(realEdges.length, 'connection', 'connections')} to it`
    );
  }
  if (triggers.length > 0) {
    consequences.push(
      `${plural(
        triggers.length,
        'trigger',
        'triggers'
      )} attached to this workflow`
    );
  }

  return {
    title,
    detail:
      consequences.length > 0
        ? `This also removes ${consequences.join(
            ' and '
          )}. Its configuration can't be recovered.`
        : "Its configuration can't be recovered.",
    confirmLabel: 'Delete',
  };
}

const WorkflowDeleteConfirmView: React.FC<{
  summary: WorkflowDeleteSummary;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ summary, onConfirm, onCancel }) => (
  <ModalPageView
    title={summary.title}
    subtitle={summary.detail}
    className="px-4 py-4"
  >
    <div className="flex justify-end gap-2">
      <Button variant="outline" onClick={onCancel}>
        Cancel
      </Button>
      <Button variant="destructive" onClick={onConfirm}>
        {summary.confirmLabel}
      </Button>
    </div>
  </ModalPageView>
);

/**
 * Confirms a graph deletion, resolving `true` to go ahead.
 *
 * Wired into the canvas as React Flow's `onBeforeDelete`, which is the one place
 * every delete route converges — the Backspace/Delete key and the toolbar button
 * (which deletes via `deleteElements`) both pass through it. Confirming on the
 * button alone would have left the keyboard, the easier one to hit by accident,
 * unguarded.
 */
export function useConfirmWorkflowDelete(): (
  deletion: WorkflowDeletion
) => Promise<boolean> {
  const modalManager = useModalManager((s) => s.actions);

  return useCallback(
    (deletion: WorkflowDeletion) => {
      const summary = summarizeWorkflowDeletion(deletion);
      if (summary === null) {
        return Promise.resolve(true);
      }

      return new Promise<boolean>((resolve) => {
        let settled = false;
        const settle = (confirmed: boolean): void => {
          if (settled) {
            return;
          }
          settled = true;
          resolve(confirmed);
        };

        modalManager.push({
          view: () => (
            <WorkflowDeleteConfirmView
              summary={summary}
              onConfirm={() => {
                settle(true);
                modalManager.pop();
              }}
              onCancel={() => {
                settle(false);
                modalManager.pop();
              }}
            />
          ),
          // Escape or a backdrop click. Without this the promise never settles and
          // React Flow waits on it forever.
          onClose: () => settle(false),
        });
      });
    },
    [modalManager]
  );
}
